import { Suspense, useEffect, useState } from "react";
import { useRoutes, Routes, Route } from "react-router-dom";
import { PrayerSourceProvider } from "./contexts/PrayerSourceContext";
import Home from "./components/home";
import routes from "tempo-routes";
import { getPrayerTimesBySource, quietLog } from "./lib/prayerTimesAPI";
import { getPrayerSource } from "./components/PrayerSourceSelector";
import { setupNotifications } from "./lib/notifications.ts";
import SettingsPage from "./components/SettingsPage";
import PrayerGuide from "./components/PrayerGuide";
import { Toaster } from "./components/ui/toaster";
import Footer from "./components/Footer";
import { useTranslation } from 'react-i18next';
import { Flowbite, DarkThemeToggle, Spinner, Dropdown } from 'flowbite-react';
import PrayerSourceDropdown from "./components/PrayerSourceDropdown";

// Custom Flowbite theme with purple/indigo colors
const customTheme = {
  navbar: {
    root: {
      base: "bg-gradient-to-r from-purple-600 to-indigo-600 px-2 py-2 sm:py-6 dark:from-purple-800 dark:to-indigo-900 sm:px-4 rounded-lg shadow-md flex flex-nowrap items-center justify-between",
    },
    brand: {
      base: "flex items-center"
    },
    collapse: {
      base: "w-full md:block md:w-auto",
      list: "mt-4 flex flex-col md:mt-0 md:flex-row md:space-x-8 md:text-sm md:font-medium",
    },
    link: {
      base: "block py-2 pr-4 pl-3 md:p-0 text-white",
      active: {
        on: "bg-purple-700 md:bg-transparent text-white md:text-purple-200",
        off: "border-b border-purple-100 hover:bg-purple-700 md:border-0 md:hover:bg-transparent"
      }
    }
  },
  darkThemeToggle: {
    root: {
      base: "rounded-lg p-2 text-sm text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-300"
    }
  },
  button: {
    color: {
      purple: "text-white bg-purple-700 hover:bg-purple-800 focus:ring-purple-300 dark:bg-purple-600 dark:hover:bg-purple-700 dark:focus:ring-purple-800 rounded-lg shadow-md",
      indigo: "text-white bg-indigo-700 hover:bg-indigo-800 focus:ring-indigo-300 dark:bg-indigo-600 dark:hover:bg-indigo-700 dark:focus:ring-indigo-800 rounded-lg shadow-md",
      gray: "text-gray-700 bg-white hover:bg-gray-100 focus:ring-gray-300 dark:bg-white dark:text-gray-800 dark:hover:bg-gray-100 dark:focus:ring-gray-300 rounded-lg shadow-md"
    }
  }
};

function App() {
  const { t, i18n } = useTranslation();
  const [notificationStatus, setNotificationStatus] = useState<string>('');

  // useEffect for notifications
  useEffect(() => {
    const loadPrayerTimes = async () => {
      try {
        // Hämta sparade inställningar
        const savedSettings = localStorage.getItem('notificationSettings');
        const notificationsEnabled = savedSettings ? JSON.parse(savedSettings).enabled : false;

        // Om notifikationer inte är aktiverade, avsluta
        if (!notificationsEnabled) {
          setNotificationStatus(t('notificationsDisabled'));
          return;
        }

        // Check if notifications are supported
        if (!("Notification" in window)) {
          setNotificationStatus(t('browserNotSupported'));
          quietLog("Browser not supporting notifications", "This browser does not support desktop notifications");
          return;
        }

        // Request permission for notifications
        if (Notification.permission !== "granted") {
          const permission = await Notification.requestPermission();
          setNotificationStatus(`${t('notificationPermission')}: ${permission}`);

          if (permission !== "granted") {
            console.warn("Notification permission not granted");
            return;
          }
        }

        const location = localStorage.getItem("selectedLocation") || "Stockholm, Sverige";
        const activeSource = getPrayerSource(); // Läs vald bönetidskälla från localStorage
        const prayerTimes = await getPrayerTimesBySource(activeSource, location);

        // Test notification to verify they're working
        new Notification("Prayer Times App", {
          body: t('notificationsConfigured'),
          icon: "/favicon.ico"
        });

        // Konfigurera aviseringar baserat på bönetider
        setupNotifications(prayerTimes);
        setNotificationStatus(t('notificationsConfiguredSuccessfully'));
      } catch (error) {
        quietLog("Kunde inte ladda bönetider eller konfigurera notifikationer", error);
        setNotificationStatus(`Error: ${error instanceof Error ? error.message : String(error)}`);
      }
    };

    loadPrayerTimes();
  }, [t]);

  const [prayerTimesData, setPrayerTimesData] = useState(null);

  useEffect(() => {
    // Check if we're within 15 minutes of Iftar time when the app loads
    const checkIfWithinIftarWindow = () => {
      if (!prayerTimesData) return;

      const now = new Date();
      const iftarTime = prayerTimesData.timings.Maghrib;
      const [iftarHour, iftarMinute] = iftarTime.split(":").map(Number);

      let iftarDate = new Date(now);
      iftarDate.setHours(iftarHour, iftarMinute, 0, 0);

      const diff = iftarDate.getTime() - now.getTime();
      const fifteenMinutesInMs = 15 * 60 * 1000;

      // If we're within 15 minutes after Iftar time
      if (diff <= 0 && Math.abs(diff) <= fifteenMinutesInMs) {
        // Show notification or trigger any necessary actions
        new Notification(t('iftarTimeNow'), {
          body: t('acceptFastMessage'),
          icon: "/favicon.ico"
        });
      }
    };

    checkIfWithinIftarWindow();
  }, [prayerTimesData, t]);

  // Function to change language
  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  return (
    <PrayerSourceProvider>
      <Flowbite theme={{ theme: customTheme }}>
        <div className="min-h-screen bg-gradient-to-b from-purple-50 to-indigo-50 dark:from-gray-900 dark:to-indigo-950 transition-colors duration-500">
          <div className="container mx-auto px-3 sm:px-6 py-3 relative z-50">
            <nav className="bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-800 dark:to-indigo-900 px-3 sm:px-5 py-2 sm:py-4 rounded-lg shadow-md border border-purple-200 dark:border-purple-800 relative z-50">
              <div className="flex items-center justify-between gap-2">

                {/* Logo */}
                <a href="/" className="flex items-center flex-shrink-0 gap-1">
                  <span className="text-purple-200 inline-block animate-pulse drop-shadow-[0_0_4px_rgba(216,180,254,0.8)] text-xl sm:text-2xl">☪</span>
                  <span className="font-bold tracking-wide text-xl sm:text-3xl bg-clip-text text-transparent bg-gradient-to-r from-purple-200 via-indigo-100 to-purple-200 font-['Montserrat',sans-serif] whitespace-nowrap">
                    <span className="hidden sm:inline">{t('prayerApp')}</span>
                    <span className="inline sm:hidden">Salah</span>
                  </span>
                </a>

                {/* Right-side actions */}
                <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
                  {/* Language buttons */}
                  <div className="flex items-center gap-0.5 sm:gap-1">
                    <button
                      onClick={() => changeLanguage('sv')}
                      className={`text-lg sm:text-xl p-1 sm:p-1.5 rounded-md transition-all ${i18n.language === 'sv' ? 'bg-white/25 shadow-inner' : 'opacity-50 hover:opacity-90'}`}
                      title="Svenska"
                    >🇸🇪</button>
                    <button
                      onClick={() => changeLanguage('en')}
                      className={`text-lg sm:text-xl p-1 sm:p-1.5 rounded-md transition-all ${i18n.language === 'en' ? 'bg-white/25 shadow-inner' : 'opacity-50 hover:opacity-90'}`}
                      title="English"
                    >🇬🇧</button>
                  </div>

                  {/* Prayer source dropdown */}
                  <PrayerSourceDropdown />

                  {/* Dark mode toggle */}
                  <DarkThemeToggle />
                </div>

              </div>
            </nav>
          </div>

          <Suspense fallback={
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Spinner size="xl" color="purple" aria-label="Loading content" />
                <p className="mt-2 text-purple-800 dark:text-purple-200">{t('loading')}</p>
              </div>
            </div>
          }>
            <div className="container mx-auto px-4 py-0 -mt-6">
              <div className="flex-grow">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/prayer-instruction" element={<PrayerGuide />} />
                </Routes>
                {import.meta.env.VITE_TEMPO === "true" && useRoutes(routes)}
              </div>
            </div>
          </Suspense>

          <Footer />
          <Toaster />
        </div>
      </Flowbite>
    </PrayerSourceProvider>
  );
}

export default App;